import React from 'react';
import {useNavigate} from 'react-router';
import {Link, useSearchParams} from 'react-router-dom';
import {EditOutlined, EyeInvisibleOutlined, EyeOutlined, PlusOutlined} from '@ant-design/icons';
import {useMutation, useQuery} from '@apollo/client/react';
import {Button, Input, Space, Table, Tag, Typography} from 'antd';
import dayjs from 'dayjs';
import _ from 'lodash';

import {ClientsQuery} from '../../../__generated__/graphql';
import {toAdminBalance, toAdminClient, toAdminUser} from '../../../config/routes';
import {QUERY_CLIENTS, UPDATE_CLIENT} from '../../../shared/graphql/admin/clients';
import {currencyFormat} from '../../../shared/helpers/currencyFormat';
import {phonePlainToInput} from '../../../shared/helpers/phoneFormat';

import {filtersToQuery, filtersToSearchParams, searchParamsToFilters} from './filter';

import cls from './Clients.module.scss'

type IParent = NonNullable<NonNullable<ClientsQuery['clients'][number]>['parents'][number]>

export const Clients = () => {

    const [searchParams, setSearchParams] = useSearchParams();
    const filters = searchParamsToFilters(searchParams);
    const navigate = useNavigate();

    const [updateClient] = useMutation(UPDATE_CLIENT)

    const query = useQuery(QUERY_CLIENTS, {
        variables: {
            filters: filtersToQuery(filters),
            pagination: {limit: 1000},
            sort: ['enabled:DESC', 'lastName:ASC'],
        },
        fetchPolicy: 'no-cache',
    })

    const onSearchChange = (data: any) => {
        setSearchParams(filtersToSearchParams({
            ...filters,
            search: data,
        }))
    }

    const dataSource = _.map(query.data?.clients, item => ({
        ...item,
        key: item?.documentId,
        name: `${item?.firstName} ${item?.lastName}`,
        birthday: item?.birthday ? dayjs(item.birthday).format('DD.MM.YYYY') : '—',
    }))

    const updateClientEnabled = async (id: string, enabled: boolean) => {

        await updateClient({
            variables: {id: id, data: {enabled: enabled}},
            onCompleted: () => {
                query.refetch()
            }
        })

    }

    const columns = [
        {
            title: 'Имя Фамилия',
            dataIndex: 'name',
            key: 'name',
            render: (item: any, record: any) => {
                const element = record.phone ? <Link to={toAdminBalance(record.documentId)}>{item}</Link> : item
                return record.enabled ? element : <span className={cls.hided}>{element}</span>
            },
        },
        {
            title: 'Дата рождения',
            dataIndex: 'birthday',
            key: 'birthday',
            render: (value: string, record: any) => (
                record.enabled ? value : <span className={cls.hided}>{value}</span>
            ),
        },
        {
            title: 'Номер в IIKO',
            dataIndex: 'phone',
            key: 'phone',
            render: (phone: string, record: any) => (
                record.enabled ? phonePlainToInput(phone) :
                    <span className={cls.hided}>{phonePlainToInput(phone)}</span>
            ),
        },
        {
            title: 'Баланс',
            dataIndex: 'iikoBalance',
            key: 'iikoBalance',
            sorter: (a: any, b: any) => {
                if (a.iikoBalance === null) return b.iikoBalance === null ? 0 : 1
                if (b.iikoBalance === null) return -1
                return a.iikoBalance - b.iikoBalance
            },
            render: (balance: number | null, record: any) => {
                const element = <>
                    <div>
                        <Typography.Text type={balance !== null && balance < 0 ? 'danger' : undefined}>
                            {balance !== null ? `${currencyFormat(balance)} ₽` : '—'}
                        </Typography.Text>
                    </div>
                    {record.iikoBalanceAt &&
                        <Typography.Text type={'secondary'} style={{fontSize: 12}}>
                            {dayjs(record.iikoBalanceAt).format('DD.MM.YYYY HH:mm')}
                        </Typography.Text>
                    }
                </>
                return record.enabled ? element : <span className={cls.hided}>{element}</span>
            },
        },
        {
            title: 'Родители',
            dataIndex: 'parents',
            key: 'parents',
            render: (parents: IParent[]) => (
                <Space size={[4, 4]} wrap>{_.map(parents, parent =>
                    <Link key={parent.documentId} to={toAdminUser(parent.documentId)}>
                        <Tag>
                            {parent.firstName} {parent.lastName}
                        </Tag>
                    </Link>
                )}
                </Space>),
        },
        {
            key: 'control',
            render: (_: any, record: any) => (
                <Space className={!record.enabled && cls.hided}>
                    <Button type={'link'} onClick={() => navigate(toAdminClient(record.documentId))}
                            icon={<EditOutlined/>}/>
                    {record.enabled ?
                        <Button type={'link'} onClick={() => updateClientEnabled(record.documentId, false)}
                                icon={<EyeOutlined/>}/>
                        :
                        <Button type={'link'} onClick={() => updateClientEnabled(record.documentId, true)}
                                icon={<EyeInvisibleOutlined/>}/>
                    }
                </Space>
            )
        },

    ];

    return (
        <div className={'admin-page'}>
            <div className={cls.filters}>
                <div className={cls.left}>
                    <Space>
                        <Input.Search placeholder={'Поиск...'} onSearch={onSearchChange} allowClear={true}/>
                    </Space>
                </div>
                <div className={cls.right}>
                    <Button type={'primary'} icon={<PlusOutlined/>}
                            onClick={() => navigate(toAdminClient('new'))}>Добавить</Button>
                </div>
            </div>
            <Table dataSource={dataSource} columns={columns} loading={query.loading}/>

        </div>
    )

}